import { round } from "lodash";
import { getCurrentLocale } from "./locale";

// Number formatting that reads no app state: a currency figure is formatted in
// the currency it is given. utils/numbers wraps formatNumber with the account's
// currency for callers that have no code of their own.

export const DEFAULT_CURRENCY_CODE = "USD";

export function formatNumber(
  num: number,
  precision: number = 1,
  isCurrency: boolean = false,
  compact: boolean = true,
  minPrecision?: boolean,
  locale: string = getCurrentLocale(),
  isPretty: boolean = true,
  currencyCode?: string
): { value: string; modifier: string; currencySign?: string } {
  const options: Intl.NumberFormatOptions = {
    notation: compact ? "compact" : "standard",
    compactDisplay: "short",
    minimumFractionDigits: !minPrecision ? 0 : precision,
    maximumFractionDigits: precision,
    useGrouping: isPretty
  };

  if (isCurrency) {
    options.style = "currency";
    options.currency = currencyCode || DEFAULT_CURRENCY_CODE;
    options.currencyDisplay = "narrowSymbol";
  } else {
    options.style = "decimal";
  }

  const formatter = new Intl.NumberFormat(new Intl.Locale(locale), options);
  const parts = formatter.formatToParts(num);

  let value = "";
  let modifier = "";
  let currencySign = "";

  parts.forEach((part) => {
    if (
      part.type === "integer" ||
      part.type === "decimal" ||
      part.type === "fraction" ||
      part.type === "group" ||
      part.type === "minusSign"
    ) {
      value += part.value;
    } else if (part.type === "compact") {
      modifier = part.value;
    } else if (part.type === "currency") {
      currencySign = part.value;
    }
  });

  return { value, modifier, currencySign };
}

/**
 * A percent such as "50.1%" or "6,272,182%": rounded to `precision` decimals,
 * dropping trailing zeros, and grouped in the viewer's locale.
 */
export const formatPercentage = (
  num: number,
  precision: number = 1,
  locale: string = getCurrentLocale()
): string => {
  // Rounded here rather than by Intl, which rounds the binary value and turns
  // 50.05 into "50"; `|| 0` keeps a rounded -0 from rendering as "-0".
  const rounded = round(num, precision) || 0;
  const formatted = new Intl.NumberFormat(new Intl.Locale(locale), {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision
  }).format(rounded);
  return `${formatted}%`;
};
